
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader1 from "../../components/common/PageHeader1";
import { submitSleepStoryPlaylistApi, getSleepStoryByIdApi, getSleepStoryCategories } from "../../services/SleepStories";

function CreateSleepStory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voicePreview, setVoicePreview] = useState(null);
  const [voiceError, setVoiceError] = useState("");

  useEffect(() => {
    // Fetch categories
    (async () => {
      try {
        const data = await getSleepStoryCategories();
        if (data.success) {
          setCategories(data.sleepStoryCategories || []);
          setLanguages(data.languages || []);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (id) {
      (async () => {
        const res = await getSleepStoryByIdApi(id);
        if (res?.data) {
          const r = res.data;
          setValue("title", r.title);
          setValue("description", r.description);
          setValue("duration", r.duration);
          setValue("category", r.category);
          setValue("languages", r.languages);
          setImagePreview(r.thumbnail);
          setVoicePreview(r.voice);
        }
      })();
    }
  }, [id, setValue]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleVoiceChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVoiceFile(file);
      setVoicePreview(URL.createObjectURL(file));
      setVoiceError("");
    }
  };

  const onSubmit = async (formData) => {
    if (!id && !voiceFile) {
        setVoiceError("Voice file is required");
        return;
    }

    const payload = {
      ...formData,
      imageFile,
      voiceFile,
    };
    if (id) payload._id = id;

    try {
      setLoading(true);
      const res = await submitSleepStoryPlaylistApi(payload);
      if (res) {
        if (!id) {
          reset();
          setImageFile(null);
          setImagePreview(null);
          setVoiceFile(null);
          setVoicePreview(null);
        } else {
             navigate("/sleep-stories-list"); 
        }
      }
    } catch (error) {
      console.error("Error submitting story:", error);
      toast.error("Failed to submit story. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <PageHeader1
        pagetitle={id ? "Edit Sleep Story" : "Add Sleep Story"}
        navigation="Sleep Stories"
        navlink="/sleep-stories-list"
        current={id ? "Edit" : "Add"}
      />

      <div className="col-lg-10 mx-auto">
        <div className="card p-4 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} className="row g-3">
            <div className="col-12 text-center">
              <h2>{id ? "Edit Sleep Story" : "Add New Sleep Story"}</h2>
            </div>

            <div className="col-md-6">
              <label className="form-label">Title</label>
              <input
                className="form-control"
                {...register("title", { required: "Title is required" })}
              />
              {errors.title && <p className="text-danger">{errors.title.message}</p>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Category</label>
              <select
                className="form-control"
                {...register("category", { required: "Category is required" })}
              >
                <option value="">Select Category</option>
                {categories.map((cat, index) => (
                  <option key={index} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-danger">{errors.category.message}</p>
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                {...register("description", { required: "Description is required" })}
                rows={3}
              />
              {errors.description && <p className="text-danger">{errors.description.message}</p>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Duration (Minutes)</label>
              <input
                type="number"
                className="form-control"
                {...register("duration", { required: "Duration is required" })}
              />
              {errors.duration && <p className="text-danger">{errors.duration.message}</p>}
            </div>
            
            <div className="col-md-6">
                <label className="form-label">Language</label>
                 <select
                  className="form-control"
                  {...register("languages")}
                >
                  <option value="">Select Language</option>
                  {languages.map((cat, index) => (
                    <option key={index} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
            </div>

            <div className="col-md-6">
              <label className="form-label">Thumbnail Image</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImageChange}
              />
               {/* Validation: if no existing image and no new file selected */}
               {!imagePreview && !imageFile && errors.thumbnail && <p className="text-danger">Thumbnail is required</p>}

              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="img-thumbnail mt-2"
                  style={{ maxHeight: "200px" }}
                />
              )}
            </div>

            <div className="col-md-6">
              <label className="form-label">Voice File (Audio)</label>
              <input
                type="file"
                className="form-control"
                accept="audio/*"
                onChange={handleVoiceChange}
              />
               {voiceError && <p className="text-danger">{voiceError}</p>}
              {voicePreview && (
                <audio controls className="mt-2 w-100">
                  <source src={voicePreview} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              )}
            </div>

            <div className="col-12 text-center mt-4">
              <Button type="submit" className="btn btn-primary px-5 me-3" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Saving...
                  </>
                ) : (
                  id ? "Update" : "Save"
                )}
              </Button>
              <Button
                variant="secondary"
                className="px-5"
                onClick={() => navigate("/sleep-stories-list")}
              >
                Back
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateSleepStory;
